/*
 *   File : store-detail.js
 *   Author : https://evoqins.com
 *   Description : Store detail page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Custom component 
import { Employee } from './Helper';
import { useLocation, useNavigate } from 'react-router-dom';
import { SummaryCard } from '../../../Component/Cards';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { DownloadButton, TextButton } from '../../../Component/Buttons';
import { DataTableComponent } from '../../../Component/DataTable';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { toast } from 'react-toastify';
import { AddLocationModal, ConfirmDeleteModal } from '../../../Component/Modal';
import { Modal } from 'bootstrap';

const CATEGORY_TAB = [
    {
        id: 1,
        name: "Store performance"
    },
    {
        id: 2,
        name: "Sales specialists"
    },
];

const ROWS_PER_PAGE = 20;

const PERFORMANCE_SORT_FIELDS = [
    'product_name',
    'category',
    'interior_exterior',
    'innovation_old',
    'finish',
    'total_litres_sold',
    'points',
];

const StoreDetail = () => {
    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const navigate = useNavigate();
    const { state } = useLocation();
    const tableRef = useRef(null);

    const [tabIndex, setTabIndex] = useState(0);

    const [storeId, setStoreId] = useState(null);
    const [storeDetailData, setStoreDetailData] = useState({});
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [performanceData, setPerformanceData] = useState({});
    const [performanceDataLoading, setPerformanceDataLoading] = useState(true);
    const [downloadLoader, setDownloadLoader] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [addLocationModal, setAddLocationModal] = useState(false);

    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [deleteResponse, setDeleteResponse] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (addLocationModal === true) {
            const addLocationModal = new Modal(document.getElementById('add-location-modal'));
            addLocationModal.show();
        }
    }, [addLocationModal]);

    useEffect(() => {
        if (deleteModal === true) {
            const delete_modal = new Modal(document.getElementById('confirm-delete-modal'));
            delete_modal.show();
        }
    }, [deleteModal]);

    useEffect(() => {
        if (state != null && state.store_id != null) {
            setTabIndex(state.tabIndex ?? 1);
            setStoreId(state.store_id);
        } else {
            navigate("/store-listing");
        }
    }, [state, navigate]);

    useEffect(() => {
        if (storeId !== null) {
            _storeDetail();  //API
        }
    }, [tabIndex, storeId]);


    useEffect(() => {
        if (storeId !== null) {
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            _getStorePerformance();
        }
    }, [storeId, currentPage]);

    // handle tab change 
    const _handleTabChange = (id) => {
        navigate("/store-listing/detail", {
            state: {
                tabIndex: id,
                store_id: storeId
            }
        });
        setSummaryLoading(true);
        setSortField(null);
        setSortDirection(null);
        setCurrentPage(1);
    };

    // handle table page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setPerformanceDataLoading(true);
        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PERFORMANCE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setPerformanceDataLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getStorePerformance(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };


    // API - Get store detail data
    const _storeDetail = () => {
        let is_post = true;
        let url = 'CA/store/detail';
        let data = {
            "store_id": storeId,
            "filter": tabIndex
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setStoreDetailData(response.data);
            } else { }
            setLoading(false);
            setSummaryLoading(false);
        });
    };

    // API - Get store performance summary
    const _getStorePerformance = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && PERFORMANCE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/store/product_performance_summary';
        let data = {
            "store_id": storeId,
            "page_num": page_num,
            "limit": ROWS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setPerformanceData(response.data ?? {});
            } else {
                setPerformanceData({});
            }
            setPerformanceDataLoading(false);
        }).catch(() => {
            setPerformanceData({});
            setPerformanceDataLoading(false);
        });
    };


    // API - Get store performance summary download
    const _download = () => {
        setDownloadLoader(true);
        const effective_sort_by = sortField && PERFORMANCE_SORT_FIELDS.includes(sortField) ? sortField : null;
        const effective_sort_order = effective_sort_by ? sortDirection : null;

        let is_post = true;
        let url = 'CA/store/product_performance_summary/download';
        let data = {
            "store_id": storeId,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const downloadUrl = response.data?.download_url;
                if (downloadUrl) {
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = '';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                } else {
                    toast.dismiss();
                    toast.error(response.message || "Download link unavailable", {
                        type: "error",
                        className: "e-toast",
                    });
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        }).catch(() => {
            setDownloadLoader(false);
            toast.dismiss();
            toast.error("Download failed", {
                type: "error",
                className: "e-toast",
            });
        });
    };

    // API - Delete location 
    const _deleteLocation = () => {
        setDeleteLoader(true);
        let is_post = true;
        let url = 'CA/store/remove-location-url';
        let data = {
            "store_id": storeId
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDeleteResponse(true);
            } else {
                setDeleteResponse(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDeleteLoader(false);
        });
    };


    // Table column 
    const TABLE_COLUMNS = [
        {
            name: 'Product name',
            id: 'product_name',
            sortField: 'product_name',
            selector: row => row.product_name || "-",
            cell: row => <>{row.product_name || "-"}</>,
            width: "280px",
            sortable: true
        },
        {
            name: 'Category',
            id: 'category',
            sortField: 'category',
            selector: row => row.category || "-",
            width: "120px",
            sortable: true
        },
        {
            name: 'Interior/ Exterior',
            id: 'interior_exterior',
            sortField: 'interior_exterior',
            selector: row => row.interior_exterior || "-",
            width: "160px",
            sortable: true
        },
        {
            name: 'Innovation/Old',
            id: 'innovation_old',
            sortField: 'innovation_old',
            selector: row => row.innovation_old || "-",
            width: "140px",
            sortable: true
        },
        {
            name: 'Finish',
            id: 'finish',
            sortField: 'finish',
            selector: row => row.finish || "-",
            width: "120px",
            sortable: true
        },
        {
            name: PROFILE_DATA?.country === "Egypt" ? 'Total units sold' : 'Total ltrs sold',
            id: 'total_litres_sold',
            sortField: 'total_litres_sold',
            selector: row => row.total_litres_sold || "-",
            width: "130px",
            sortable: true
        },
        {
            name: "Points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points || "-",
            width: "120px",
            sortable: true
        },

    ];

    return (
        <section className='padding-120px-bottom'>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom  padding-52px-lr'>
                <div className='e-display-flex cursor-pointer w-max-content'
                    onClick={() => navigate("/store-listing")} >
                    <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                        draggable={false}
                        className='cursor-pointer'
                        alt='arrow'
                    />
                    <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Store detail
                    </h1>
                </div>
            </div>

            {/* Admin details */}
            <div className='margin-neg-66px-top  padding-52px-lr'>

                {loading ?
                    // loader 
                    <Loader />
                    :
                    <>
                        <div className='e-admin-box-shadow e-bg-white e-border-radius-16 padding-32px-all'>
                            <div className='row'>
                                <div className='col-md-5 col-12 text-xl-start text-lg-start text-md-start text-center my-auto'>
                                    <h3 className='mb-0 e-primary-font-playfair e-text-charleston-green e-font-24 e-font-weight-600 line-height-32px'>
                                        {storeDetailData.name}
                                    </h3>
                                </div>
                                <div className='col-md-7 col-12 d-flex justify-content-end'>
                                    <div className='e-bg-beautiful-white e-border-radius-8 p-3' style={{ width: "444px" }}>
                                        <div className='d-flex gap-54px align-items-center justify-content-between'>
                                            <div className='d-flex flex-column gap-2'>
                                                <p className='mb-0  e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-500 line-height-20px'>
                                                    Store ID: <span className='e-text-raisin-black e-font-weight-600'>{storeDetailData.id || "-"}</span>
                                                </p>
                                                <p className='mb-0   e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-500 line-height-20px d-flex align-items-center '>
                                                    Location: <span className='e-text-raisin-black e-font-weight-600 ps-1'>{storeDetailData.location || "-"}</span>
                                                    {storeDetailData.location_url && storeDetailData.location_url !== null ?
                                                        <div className='d-flex align-items-center '>
                                                            <span className='ps-1 e-view-map-tooltip cursor-pointer d-inline-flex align-items-center position-relative'>
                                                                <img
                                                                    src={require("../../../Assets/Image/CountryAdmin/view-map.svg").default}
                                                                    height={16}
                                                                    width={16}
                                                                    alt='view-map'
                                                                />
                                                                <div className="position-absolute  padding-12px-all e-view-map-tooltip-content e-bg-white e-border-radius-8">
                                                                    <a href={storeDetailData.location_url} target='_blank' rel="noopener noreferrer" className='e-text-rich-black-light e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px text-decoration-none' >
                                                                        View map
                                                                    </a>
                                                                </div>
                                                            </span>

                                                            <span className='ps-2 e-font-14 e-text-indigo e-font-weight-400 cursor-pointer text-decoration-hover-underline d-inline-flex w-max-content'
                                                                onClick={() => setAddLocationModal(true)}>Edit</span>

                                                            <TextButton name="Delete"
                                                                className="e-text-lust ps-2"
                                                                fontWeight="e-font-weight-400"
                                                                fontSize="e-font-14"
                                                                onPress={() => { setDeleteModal(true); }} />
                                                        </div>
                                                        :
                                                        <span className='ps-2 e-text-indigo e-font-weight-400 cursor-pointer text-decoration-underline d-inline-flex w-max-content'
                                                            onClick={() => setAddLocationModal(true)}>Add link</span>

                                                    }
                                                </p>
                                                <p className='mb-0  e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-500 line-height-20px'>
                                                    Contact: <span className='e-text-raisin-black e-font-weight-600'>{storeDetailData.mobile || "-"}</span>
                                                </p>
                                                <p className='mb-0  e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-500 line-height-20px'>
                                                    Country manager: <span className='e-text-raisin-black e-font-weight-600'>{"-"}</span>
                                                </p>
                                            </div>

                                            <div className='vr e-text-st-louis my-auto' style={{ height: "64px" }}></div>

                                            <div className='d-flex flex-column gap-3'>
                                                <p className='mb-0  e-alt-font-noto-sans e-text-davys-grey e-font-14 e-font-weight-500 line-height-20px'>
                                                    Overall sales
                                                </p>
                                                <p className='mb-0  e-alt-font-noto-sans e-text-raisin-black e-font-14 e-font-weight-600 line-height-20px'>
                                                    {storeDetailData.overall_sales} <span className='e-font-weight-400'>{storeDetailData.overall_sales === null ? '-' : PROFILE_DATA?.country === "Egypt" ? "(Sales per units)" : "(Sales per ltrs)"}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* category tab */}
                        <div className='d-flex gap-0-16 my-4 overflow-auto'>
                            {CATEGORY_TAB.map((data, key) => (
                                <div key={key} className={`${tabIndex === data.id ? 'e-bg-lust e-text-white pe-none' : 'e-text-rich-black'} e-border-1px-cool-grey e-border-radius-24 cursor-pointer`}
                                    onClick={() => _handleTabChange(data.id)} >
                                    <p className='mb-0 px-4 padding-12px-tb e-alt-font-noto-sans  e-font-14 e-font-weight-500 line-height-14px w-max-content'>
                                        {data.name}
                                    </p>
                                </div>
                            ))}
                        </div>


                        {/* Summary  */}
                        {tabIndex === 1 ?
                            <>
                                <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                                    <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                                        Summary
                                    </h4>

                                    {/* Summary cards */}
                                    <div className='row row-cols-xl-4 row-cols-lg-3 row-cols-md-3 row-cols-sm-2 row-cols-1 row-gap-3 padding-10px-top'>
                                        <div className='col m-0'>
                                            <SummaryCard title="No. of sales specialists"
                                                loader={summaryLoading}
                                                quantity={storeDetailData?.data?.sales_specialist} />
                                        </div>
                                        <div className='col m-0'>
                                            <SummaryCard title={PROFILE_DATA?.country === "Egypt" ? "Total volume in units" : "Total volume in litres"}
                                                loader={summaryLoading}
                                                quantity={storeDetailData?.data?.total_sale} />
                                        </div>
                                        <div className='col m-0'>
                                            <SummaryCard title={PROFILE_DATA?.country === "Egypt" ? "Premium sales in units" : "Premium sales in litres"}
                                                loader={summaryLoading}
                                                quantity={storeDetailData?.data?.premium_sale} />
                                        </div>
                                        {/* <div className='col m-0'>
                                        <SummaryCard title="Painters visited"
                                            loader={summaryLoading}
                                            quantity={storeDetailData.data.painter_visited} />
                                    </div> */}

                                        <div className='col m-0'>
                                            <SummaryCard title="Total points"
                                                loader={summaryLoading}
                                                quantity={storeDetailData.data.reward_earned} />
                                        </div>
                                    </div>
                                </div>


                                {/* Store performance summary */}
                                <div ref={tableRef} className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                                    <div className='row rpw-gap-2 px-4'>
                                        <div className='  col-md-6 col-sm-12'>
                                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px' >
                                                Store performance summary
                                            </h4>
                                        </div>
                                        <div className='col-md-6 col-sm-8 col-12 text-end'>
                                            <div className='d-flex align-items-center gap-4 justify-content-end'>
                                                <DownloadButton loader={downloadLoader}
                                                    disabled={performanceDataLoading || Object.keys(performanceData).length === 0 || performanceData?.data?.length === 0}
                                                    onClick={_download} />
                                            </div>
                                        </div>
                                    </div>

                                    {performanceDataLoading ?
                                        // loader 
                                        <Loader />
                                        :
                                        <>
                                            {/* registered members data table */}
                                            {Object.keys(performanceData).length !== 0 && performanceData?.data?.length !== 0 ?
                                                <div className='e-table'>
                                                    <DataTableComponent
                                                        key={`store-performance-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                        columns={TABLE_COLUMNS}
                                                        data={performanceData.data}
                                                        pagination={true}
                                                        paginationServer={true}
                                                        paginationPerPage={ROWS_PER_PAGE}
                                                        paginationTotalRows={performanceData.total_count}
                                                        paginationDefaultPage={currentPage}
                                                        paginationResetDefaultPage={false}
                                                        sortServer={true}
                                                        defaultSortFieldId={sortField}
                                                        defaultSortAsc={sortDirection !== 'desc'}
                                                        onSort={_handleSort}
                                                        onChangePage={_handlePageChange} />
                                                </div>
                                                :
                                                // empty screen
                                                <div className='h-100vh pt-5 mt-5'>
                                                    <EmptyScreen message="No data found" />
                                                </div>
                                            }
                                        </>

                                    }
                                </div>
                            </>
                            :
                            // employee details
                            <Employee storeId={storeId}
                                loader={summaryLoading}
                                summaryData={storeDetailData?.data ?? {}} />
                        }
                    </>
                }
            </div>

            {/* Add location modal */}
            {addLocationModal === true &&
                <AddLocationModal
                    id={storeId}
                    locationUrl={storeDetailData?.location_url}
                    handleClose={() => {
                        setTimeout(() => {
                            setAddLocationModal(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setAddLocationModal(false);
                            _storeDetail();
                        }, 200);
                    }} />
            }



            {deleteModal === true &&
                <ConfirmDeleteModal label="location"
                    deleteId={storeId}
                    loader={deleteLoader}
                    response={deleteResponse}
                    delete={_deleteLocation}
                    success={() => {
                        setTimeout(() => {
                            setDeleteModal(false);
                            setDeleteResponse(false);
                            _storeDetail();
                        }, 200);
                    }}
                    handleClose={() => {
                        setDeleteModal(false);
                        setDeleteResponse(false);
                    }} />
            }
        </section>
    )
}

export default StoreDetail;